import { EXTERNAL_ROUTES } from '@/constants';
import { ImageWithFallback } from '@/modules/common/components/ImageWithFallback';
import { ICohort, ICourseWithEducationProvider } from '@/types/course';
import {
  AccessTime,
  CalendarTodayOutlined,
  ComputerOutlined,
  LabelOutlined,
  OpenInNewOutlined,
} from '@mui/icons-material';
import { Button, Chip, Typography } from '@mui/material';
import { ComponentProps } from 'react';

type CourseCardProps = {
  course: ICourseWithEducationProvider;
  selectedCohort?: ICohort;
} & ComponentProps<'div'>;

export function CourseCard({
  course,
  selectedCohort,
  ...props
}: CourseCardProps) {
  return (
    <div
      {...props}
      className={`flex flex-col gap-4 p-4 rounded-lg border ${props.className ?? ''}`}
    >
      <div className="flex gap-6">
        <ImageWithFallback
          src={course?.imageUrl}
          alt={course?.name}
          fallbackSrc="/images/no-applications.png"
          style={{
            width: 128,
            height: 128,
            borderRadius: 8,
            objectFit: 'cover',
          }}
        />
        <div className="flex flex-col gap-4">
          <Typography variant="h3">{course?.name}</Typography>
          <div className="flex justify-start items-center gap-6 text-gray-500 text-base">
            {selectedCohort?.duration?.quantity && (
              <span className="flex items-center gap-1">
                <CalendarTodayOutlined className="w-5 h-5" />
                <Typography variant="subtitle2" color="text.secondary">
                  {`${selectedCohort.duration.quantity} ${selectedCohort.duration.type}`}
                </Typography>
              </span>
            )}
            {selectedCohort?.format && (
              <span className="flex items-center gap-1">
                <ComputerOutlined className="w-5 h-5" />
                <Typography
                  className="capitalize"
                  variant="subtitle2"
                  color="text.secondary"
                >
                  {selectedCohort.format}
                </Typography>
              </span>
            )}
            {course?.level && (
              <span className="flex items-center gap-1">
                <LabelOutlined className="w-5 h-5" />
                <Typography
                  className="capitalize"
                  variant="subtitle2"
                  color="text.secondary"
                >
                  {course.level}
                </Typography>
              </span>
            )}
            {selectedCohort?.classType && (
              <span className="flex items-center gap-1">
                <AccessTime className="w-5 h-5" />
                <Typography
                  className="capitalize"
                  variant="subtitle2"
                  color="text.secondary"
                >
                  {selectedCohort.classType}
                </Typography>
              </span>
            )}
          </div>
          <div className="flex gap-4 items-center">
            {course?.categories?.[0]?.name && (
              <Chip
                label={course.categories[0].name}
                className="capitalize mt-1"
                size="small"
                color="primary"
              />
            )}
            <Button
              variant="outlined"
              startIcon={<OpenInNewOutlined />}
              href={EXTERNAL_ROUTES.MARKETPLACE_COURSE_URL.replace(
                ':id',
                course._id!,
              )}
              target="_blank"
              rel="noopener noreferrer"
            >
              View course
            </Button>
          </div>
        </div>
      </div>
      {course?.educationProvider && (
        <div className="flex flex-row items-center gap-2">
          <div className="rounded-full overflow-hidden">
            <ImageWithFallback
              src={course.educationProvider.logoUrl}
              alt={course.educationProvider.name}
              fallbackSrc="/images/no-applications.png"
              style={{
                width: 52,
                height: 52,
                borderRadius: 8,
                objectFit: 'cover',
                border: '1px solid #eee',
                background: 'white',
              }}
            />
          </div>
          <Typography variant="h3" fontWeight={600}>
            {course.educationProvider.name}
          </Typography>
        </div>
      )}
      {course?.summary && (
        <Typography
          variant="subtitle1"
          color="text.secondary"
          className="overflow-hidden text-ellipsis line-clamp-2"
        >
          {course.summary}
        </Typography>
      )}
    </div>
  );
}
